import React, { useEffect } from 'react';
import { categories } from '../quizBank';
import { saveQuizAttempt, getBestScore } from '../utils/quizHistory';

/**
 * QuizResults - Results screen for Golden Quiz
 *
 * Features:
 * - Score display with percentage
 * - Performance feedback
 * - Question-by-question review
 * - Options to retry or return to dashboard
 */
function QuizResults({ results, onRetry, onBackToDashboard }) {
  const { categoryId, difficulty, score, totalQuestions, answeredQuestions, timedMode, timeBonus } = results;
  const category = categories.find(c => c.id === categoryId);
  const basePercentage = Math.round((score / totalQuestions) * 100);
  const percentage = timedMode && timeBonus ? Math.min(100, basePercentage + timeBonus) : basePercentage;

  // Save quiz attempt to history on mount
  useEffect(() => {
    saveQuizAttempt({
      categoryId,
      difficulty,
      score,
      totalQuestions,
      percentage,
      answeredQuestions
    });
  }, [categoryId, difficulty, score, totalQuestions, percentage, answeredQuestions]);

  // Get best score for comparison
  const bestScore = getBestScore(categoryId, difficulty);
  const isNewBest = !bestScore || percentage >= ((bestScore.score / bestScore.totalQuestions) * 100);

  // Performance assessment
  const getPerformanceLevel = (percent) => {
    if (percent >= 90) return { level: 'Exceptional', color: '#22C55E', emoji: '🌟', message: 'Outstanding mastery!' };
    if (percent >= 80) return { level: 'Excellent', color: '#10B981', emoji: '🎯', message: 'Strong understanding!' };
    if (percent >= 70) return { level: 'Good', color: '#F59E0B', emoji: '👍', message: 'Solid performance!' };
    if (percent >= 60) return { level: 'Fair', color: '#F97316', emoji: '📚', message: 'Room for improvement.' };
    return { level: 'Needs Review', color: '#EF4444', emoji: '📖', message: 'Consider reviewing the material.' };
  };

  const performance = getPerformanceLevel(percentage);

  const difficultyColors = {
    easy: { bg: '#22C55E20', border: '#22C55E', text: '#22C55E' },
    medium: { bg: '#F59E0B20', border: '#F59E0B', text: '#F59E0B' },
    hard: { bg: '#EF444420', border: '#EF4444', text: '#EF4444' }
  };

  const difficultyColor = difficultyColors[difficulty];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0A0F1C',
      color: '#F5F5F5',
      paddingBottom: '60px'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
        {/* Header */}
        <header style={{ marginBottom: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '15px'
          }}>
            <div>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: '700',
                color: '#D4AF37',
                marginBottom: '5px'
              }}>
                Quiz Complete!
              </h1>
              <p style={{
                fontSize: '1.1rem',
                color: '#A0A0A0'
              }}>
                {category.icon} {category.name}
              </p>
            </div>
            <button
              onClick={onBackToDashboard}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2A2A2A',
                color: '#F5F5F0',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2A2A2A'}
            >
              ← Back to Dashboard
            </button>
          </div>
        </header>

        {/* Score Card */}
        <section style={{ marginBottom: '40px' }}>
          <div style={{
            backgroundColor: '#1A1F2E',
            borderRadius: '20px',
            padding: '50px',
            textAlign: 'center',
            border: `3px solid ${performance.color}`
          }}>
            {/* Performance Emoji */}
            <div style={{
              fontSize: '5rem',
              marginBottom: '20px'
            }}>
              {performance.emoji}
            </div>

            {/* Score Display */}
            <div style={{
              fontSize: '4rem',
              fontWeight: '800',
              color: performance.color,
              marginBottom: '10px'
            }}>
              {percentage}%
            </div>

            <div style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#F5F5F5',
              marginBottom: '15px'
            }}>
              {score} out of {totalQuestions} correct
              {timedMode && timeBonus > 0 && (
                <span style={{
                  marginLeft: '10px',
                  fontSize: '1rem',
                  color: '#22C55E'
                }}>
                  +{timeBonus}% ⏱️ Time Bonus
                </span>
              )}
              {isNewBest && (
                <span style={{
                  marginLeft: '15px',
                  fontSize: '1.2rem',
                  color: '#FFD700'
                }}>
                  🏆 New Best!
                </span>
              )}
            </div>

            {/* Performance Level */}
            <div style={{
              display: 'inline-block',
              padding: '10px 24px',
              backgroundColor: `${performance.color}20`,
              border: `2px solid ${performance.color}`,
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: '700',
              color: performance.color,
              marginBottom: '15px'
            }}>
              {performance.level}
            </div>

            <p style={{
              fontSize: '1rem',
              color: '#A0A0A0',
              marginBottom: '30px'
            }}>
              {performance.message}
            </p>

            {/* Difficulty Badge */}
            <div style={{
              display: 'inline-block',
              padding: '8px 16px',
              backgroundColor: difficultyColor.bg,
              border: `2px solid ${difficultyColor.border}`,
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: difficultyColor.text,
              textTransform: 'capitalize'
            }}>
              {difficulty} difficulty
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        <section style={{
          display: 'flex',
          gap: '15px',
          marginBottom: '50px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={onRetry}
            style={{
              padding: '14px 32px',
              backgroundColor: category.color,
              color: '#0A0F1C',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${category.color}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            🔄 Retry Quiz
          </button>

          <button
            onClick={onBackToDashboard}
            style={{
              padding: '14px 32px',
              backgroundColor: '#2A2A2A',
              color: '#F5F5F0',
              border: '2px solid #3A3A3A',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#3A3A3A';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2A2A2A';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            📊 Back to Categories
          </button>
        </section>

        {/* Question Review */}
        <section>
          <h2 style={{
            fontSize: '1.8rem',
            fontWeight: '700',
            color: '#F5F5F5',
            marginBottom: '25px'
          }}>
            Review Your Answers
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            {answeredQuestions.map((q, index) => (
              <div
                key={q.id}
                style={{
                  backgroundColor: '#1A1F2E',
                  borderRadius: '12px',
                  padding: '25px',
                  border: `2px solid ${q.isCorrect ? '#22C55E40' : '#EF444440'}`
                }}
              >
                {/* Question Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '15px',
                  gap: '15px'
                }}>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#F5F5F5',
                    lineHeight: '1.5',
                    flex: 1
                  }}>
                    <span style={{
                      color: '#808080',
                      fontWeight: '500',
                      marginRight: '10px'
                    }}>
                      Q{index + 1}.
                    </span>
                    {q.question}
                  </h3>
                  <div style={{
                    fontSize: '1.8rem',
                    flexShrink: 0
                  }}>
                    {q.isCorrect ? '✓' : '✗'}
                  </div>
                </div>

                {/* Answer Review */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  marginBottom: '15px'
                }}>
                  {q.options.map((option, optIndex) => {
                    const isUserAnswer = optIndex === q.userAnswer;
                    const isCorrectAnswer = optIndex === q.correctAnswer;
                    const showThis = isUserAnswer || isCorrectAnswer;

                    if (!showThis) return null;

                    return (
                      <div
                        key={optIndex}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: isCorrectAnswer ? '#22C55E20' : '#EF444420',
                          border: `2px solid ${isCorrectAnswer ? '#22C55E' : '#EF4444'}`,
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          color: isCorrectAnswer ? '#22C55E' : '#EF4444',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}
                      >
                        <span style={{ fontWeight: '700' }}>
                          {isCorrectAnswer && isUserAnswer ? '✓ Your answer (Correct)' :
                           isCorrectAnswer ? '✓ Correct answer' :
                           '✗ Your answer'}:
                        </span>
                        <span style={{ color: '#F5F5F5' }}>{option}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                <div style={{
                  padding: '15px',
                  backgroundColor: '#0F1419',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${category.color}`
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '700',
                    color: '#A0A0A0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px'
                  }}>
                    Explanation
                  </div>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#C0C0C0',
                    lineHeight: '1.6',
                    margin: 0
                  }}>
                    {q.explanation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default QuizResults;
